/**
 * Expression Evaluator Service
 *
 * Evaluates expressions in component properties, enabling data binding between components.
 * Supports syntax like: {{componentId.properties.data[0].value}}%
 */

import { Component } from "@/types";
import {
  create,
  evaluateDependencies,
  addDependencies,
  subtractDependencies,
  multiplyDependencies,
  divideDependencies,
  unaryMinusDependencies,
  unaryPlusDependencies,
  minDependencies,
  maxDependencies,
  absDependencies,
  roundDependencies,
  floorDependencies,
  ceilDependencies,
  modDependencies,
} from 'mathjs/number';

// ALLOWLIST: Restricted mathjs instance with ONLY safe arithmetic functions.
// Functions not imported here (import, evaluate, parse, simplify, derivative) don't exist.
const restrictedMath = create({
  evaluateDependencies,
  addDependencies,
  subtractDependencies,
  multiplyDependencies,
  divideDependencies,
  unaryMinusDependencies,
  unaryPlusDependencies,
  minDependencies,
  maxDependencies,
  absDependencies,
  roundDependencies,
  floorDependencies,
  ceilDependencies,
  modDependencies,
});

restrictedMath.import({
  dateDiff: (laterDate: string | number | Date, earlierDate: string | number | Date): number => {
    const later = new Date(laterDate);
    const earlier = new Date(earlierDate);
    if (isNaN(later.getTime()) || isNaN(earlier.getTime())) {
      throw new Error('dateDiff received an invalid date');
    }
    return (later.getTime() - earlier.getTime()) / (1000 * 60 * 60 * 24);
  },
}, { override: true });

export interface ExpressionContext {
  components: Component[];
  currentComponent?: Component;
}

export interface MathExpressionContext {
  currentComponentId?: string;
  currentQueryData?: Record<string, unknown>[] | null;
  dependencyData?: Record<string, Record<string, unknown>[]>;
  allComponents?: Component[];
}

export type MathReferenceStatus =
  | 'resolved'
  | 'invalid-reference'
  | 'missing-dependency-data'
  | 'no-rows'
  | 'missing-value'
  | 'non-numeric-or-invalid-date';

export interface MathReferenceDiagnostic {
  reference: string;
  dependencyId?: string;
  path?: string;
  rowCount?: number;
  rowKeys?: string[];
  status: MathReferenceStatus;
  resolvedValueType?: string;
}

export class ExpressionEvaluator {
  private static readonly AGGREGATION_PREFIX_REGEX = /^(last|first|sum|avg|min|max|count)_(.+)$/i;

  /**
   * Expression pattern: {{expression}}
   * Examples:
   * - {{donut-chart.properties.data[0].value}}
   * - {{chart-1.properties.data[0].name}}
   */
  private static readonly EXPRESSION_PATTERN = /\{\{([^}]+)\}\}/g;

  /**
   * Evaluates a string that may contain expressions
   * @param value - The string value that may contain {{expressions}}
   * @param context - The context containing all components
   * @returns The evaluated string with expressions replaced by values
   */
  static evaluate(value: string, context: ExpressionContext): string {
    if (typeof value !== 'string') {
      return value;
    }

    // Check if there are any expressions
    if (!this.EXPRESSION_PATTERN.test(value)) {
      return value;
    }

    // Reset regex state
    this.EXPRESSION_PATTERN.lastIndex = 0;

    // Replace all expressions with their evaluated values
    return value.replace(this.EXPRESSION_PATTERN, (match, expression) => {
      try {
        const result = this.evaluateExpression(expression.trim(), context);
        return result !== undefined && result !== null ? String(result) : match;
      } catch (error) {
        console.warn(`Failed to evaluate expression: ${expression}`, error);
        return match; // Return original expression if evaluation fails
      }
    });
  }

  /**
   * Evaluates a single expression
   * @param expression - The expression to evaluate (without {{ }})
   * @param context - The context containing all components
   * @returns The evaluated value
   */
  private static evaluateExpression(expression: string, context: ExpressionContext): unknown {
    // Handle component reference: componentId.properties.data[0].value
    const componentMatch = expression.match(/^([a-zA-Z0-9-_]+)\.(.+)$/);

    if (componentMatch) {
      const [, componentId, path] = componentMatch;
      const component = context.components.find(c => c.id === componentId);

      if (!component) {
        console.warn(`Component not found: ${componentId}`);
        return undefined;
      }

      return this.resolvePath(component, path);
    }

    // Handle special keywords
    if (expression === 'this' && context.currentComponent) {
      return context.currentComponent;
    }

    return undefined;
  }

  /**
   * Resolves a path like "properties.data[0].value" on an object
   * @param obj - The object to traverse
   * @param path - The path string
   * @returns The value at the path
   */
  private static resolvePath(obj: any, path: string): unknown {
    const segments = this.parsePath(path);

    let current = obj;
    for (const segment of segments) {
      if (current === null || current === undefined) {
        return undefined;
      }

      if (typeof segment === 'number') {
        // Array index
        if (!Array.isArray(current)) {
          return undefined;
        }
        current = current[segment];
      } else {
        // Object property
        current = current[segment];
      }
    }

    return current;
  }

  private static normalizeFieldKey(value: string): string {
    return value.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private static camelizeFieldKey(value: string): string {
    return value.replace(/_([a-z0-9])/gi, (_, ch: string) => ch.toUpperCase());
  }

  private static resolveRowField(row: Record<string, unknown>, field: string): unknown {
    if (field in row) return row[field];

    const normalizedField = this.normalizeFieldKey(field);
    for (const [key, value] of Object.entries(row)) {
      if (this.normalizeFieldKey(key) === normalizedField) {
        return value;
      }
    }

    const aggregationMatch = field.match(this.AGGREGATION_PREFIX_REGEX);
    if (aggregationMatch) {
      const aggregation = aggregationMatch[1];
      const rawField = aggregationMatch[2];
      const candidates = [
        rawField,
        this.camelizeFieldKey(rawField),
        `${rawField}_${aggregation}`,
        `${this.camelizeFieldKey(rawField)}_${aggregation}`,
      ];

      for (const candidate of candidates) {
        if (candidate in row) return row[candidate];
        const normalizedCandidate = this.normalizeFieldKey(candidate);
        for (const [key, value] of Object.entries(row)) {
          if (this.normalizeFieldKey(key) === normalizedCandidate) {
            return value;
          }
        }
      }
    }

    const suffixAggregationMatch = field.match(/^(.+)_(last|first|sum|avg|min|max|count)$/i);
    if (suffixAggregationMatch) {
      const rawField = suffixAggregationMatch[1];
      const aggregation = suffixAggregationMatch[2];
      const candidates = [
        rawField,
        this.camelizeFieldKey(rawField),
        `${aggregation}_${rawField}`,
        `${aggregation}_${this.camelizeFieldKey(rawField)}`,
      ];

      for (const candidate of candidates) {
        if (candidate in row) return row[candidate];
        const normalizedCandidate = this.normalizeFieldKey(candidate);
        for (const [key, value] of Object.entries(row)) {
          if (this.normalizeFieldKey(key) === normalizedCandidate) {
            return value;
          }
        }
      }
    }

    if ('value' in row) return row.value;

    const meaningfulEntries = Object.entries(row).filter(([key]) => key !== '_id' && key !== 'id');
    if (meaningfulEntries.length === 1) {
      return meaningfulEntries[0][1];
    }

    return undefined;
  }

  private static resolveMathReferenceFromRows(
    rows: Record<string, unknown>[] | null | undefined,
    path: string,
  ): unknown {
    const direct = this.resolvePath({ queryData: rows, result: rows }, path);
    if (direct !== undefined && direct !== null) return direct;

    const refMatch = path.match(/^(queryData|result)\[(\d+)\]\.([a-zA-Z0-9_]+)$/);
    if (!refMatch) return direct;

    const index = Number(refMatch[2]);
    const field = refMatch[3];
    const row = rows?.[index];
    if (!row || typeof row !== 'object' || Array.isArray(row)) return direct;

    return this.resolveRowField(row as Record<string, unknown>, field);
  }

  /**
   * Parses a path string into segments
   * Handles: properties.data[0].value => ['properties', 'data', 0, 'value']
   * @param path - The path string to parse
   * @returns Array of path segments (strings and numbers)
   */
  private static parsePath(path: string): (string | number)[] {
    const segments: (string | number)[] = [];
    let current = '';
    let inBracket = false;

    for (let i = 0; i < path.length; i++) {
      const char = path[i];

      if (char === '[') {
        if (current) {
          segments.push(current);
          current = '';
        }
        inBracket = true;
      } else if (char === ']') {
        if (inBracket && current) {
          const index = parseInt(current, 10);
          if (!isNaN(index)) {
            segments.push(index);
          }
          current = '';
        }
        inBracket = false;
      } else if (char === '.' && !inBracket) {
        if (current) {
          segments.push(current);
          current = '';
        }
      } else {
        current += char;
      }
    }

    if (current) {
      segments.push(current);
    }

    return segments;
  }

  /**
   * Checks if a value contains expressions
   * @param value - The value to check
   * @returns True if the value contains expressions
   */
  static hasExpressions(value: any): boolean {
    if (typeof value !== 'string') {
      return false;
    }

    this.EXPRESSION_PATTERN.lastIndex = 0;
    return this.EXPRESSION_PATTERN.test(value);
  }

  /**
   * Extracts all component IDs referenced in expressions
   * @param value - The string value containing expressions
   * @returns Array of component IDs referenced
   */
  static extractComponentReferences(value: string): string[] {
    if (typeof value !== 'string') {
      return [];
    }

    const refs = new Set<string>();
    this.EXPRESSION_PATTERN.lastIndex = 0;

    let match;
    while ((match = this.EXPRESSION_PATTERN.exec(value)) !== null) {
      const expression = match[1].trim();
      const componentMatch = expression.match(/^([a-zA-Z0-9-_]+)\./);
      if (componentMatch && componentMatch[1] !== 'this') {
        refs.add(componentMatch[1]);
      }
    }

    return Array.from(refs);
  }

  /**
   * Evaluates a math expression with component data references.
   * Parallel code path to evaluate() — returns number | null instead of string.
   * References use {{componentId.queryData[0].field}} or {{this.queryData[0].field}} syntax.
   * Returns null on any error (unresolved ref, NaN, Infinity, parse failure).
   */
  static evaluateMathExpression(
    expression: string,
    context: MathExpressionContext
  ): number | null {
    try {
      if (!expression || typeof expression !== 'string') return null;

      // Circular reference detection
      if (context.allComponents && context.currentComponentId) {
        if (this.detectCycle(context.currentComponentId, context.allComponents)) {
          console.warn(`[ExpressionEvaluator] Circular dependency detected for ${context.currentComponentId}`);
          return null;
        }
      }

      // Resolve all {{...}} references to numeric values
      const pattern = /\{\{([^}]+)\}\}/g;
      let resolved = expression;
      let refMatch;

      while ((refMatch = pattern.exec(expression)) !== null) {
        const ref = refMatch[1].trim();
        // Strip || fallback syntax (e.g., "comp.queryData[0].field || 0")
        // LLMs generate this but parsePath treats "|| 0" as part of the property name.
        const cleanRef = ref.replace(/\s*\|\|\s*.+$/, '').trim();
        let value: unknown;

        if (cleanRef.startsWith('this.')) {
          // Self-reference: resolve from current component's own query data
          const path = cleanRef.slice(5); // remove 'this.'
          value = this.resolveMathReferenceFromRows(context.currentQueryData, path);
        } else {
          // Cross-component or direct query-template reference: {{dependencyId.path}}
          const compMatch = cleanRef.match(/^([a-zA-Z0-9-_]+)\.(.+)$/);
          if (compMatch) {
            const [, compId, path] = compMatch;
            const depData = context.dependencyData?.[compId];
            if (depData) {
              value = this.resolveMathReferenceFromRows(depData, path);
            }
          }
        }

        const replacement = this.serializeMathReferenceValue(value);
        if (replacement === null) {
          return null; // Unresolved reference — can't evaluate
        }
        resolved = resolved.replace(refMatch[0], replacement);
      }

      // Evaluate pure math via restricted mathjs instance
      const result = restrictedMath.evaluate(resolved);
      if (typeof result !== 'number' || isNaN(result) || !isFinite(result)) {
        return null;
      }
      return result;
    } catch (error) {
      console.warn(`[ExpressionEvaluator] Math evaluation failed for: ${expression}`, error);
      return null;
    }
  }

  /**
   * Inspects the same references evaluateMathExpression() would resolve, without
   * executing the expression. Used by runtime diagnostics to distinguish empty
   * dependency data from bad formulas.
   */
  static inspectMathReferences(
    expression: string,
    context: MathExpressionContext
  ): MathReferenceDiagnostic[] {
    if (!expression || typeof expression !== 'string') return [];

    const diagnostics: MathReferenceDiagnostic[] = [];
    const pattern = /\{\{([^}]+)\}\}/g;
    let refMatch;

    while ((refMatch = pattern.exec(expression)) !== null) {
      const reference = refMatch[1].trim();
      const cleanRef = reference.replace(/\s*\|\|\s*.+$/, '').trim();
      const diagnostic = this.inspectMathReference(cleanRef, context);
      diagnostics.push({
        reference: cleanRef,
        ...diagnostic,
      });
    }

    return diagnostics;
  }

  private static inspectMathReference(
    cleanRef: string,
    context: MathExpressionContext
  ): Omit<MathReferenceDiagnostic, 'reference'> {
    if (cleanRef.startsWith('this.')) {
      const path = cleanRef.slice(5);
      return this.inspectMathReferenceRows('this', path, context.currentQueryData);
    }

    const compMatch = cleanRef.match(/^([a-zA-Z0-9-_]+)\.(.+)$/);
    if (!compMatch) {
      return { status: 'invalid-reference' };
    }

    const [, dependencyId, path] = compMatch;
    const depData = context.dependencyData?.[dependencyId];
    if (!depData) {
      return {
        dependencyId,
        path,
        status: 'missing-dependency-data',
      };
    }

    return this.inspectMathReferenceRows(dependencyId, path, depData);
  }

  private static inspectMathReferenceRows(
    dependencyId: string,
    path: string,
    rows: Record<string, unknown>[] | null | undefined,
  ): Omit<MathReferenceDiagnostic, 'reference'> {
    const rowCount = Array.isArray(rows) ? rows.length : 0;
    const rowKeys = rows?.[0] && typeof rows[0] === 'object'
      ? Object.keys(rows[0]).slice(0, 20)
      : [];

    if (!rows) {
      return {
        dependencyId,
        path,
        rowCount,
        rowKeys,
        status: 'missing-dependency-data',
      };
    }

    if (rowCount === 0) {
      return {
        dependencyId,
        path,
        rowCount,
        rowKeys,
        status: 'no-rows',
      };
    }

    const value = this.resolveMathReferenceFromRows(rows, path);
    if (value === undefined || value === null) {
      return {
        dependencyId,
        path,
        rowCount,
        rowKeys,
        status: 'missing-value',
      };
    }

    if (this.serializeMathReferenceValue(value) === null) {
      return {
        dependencyId,
        path,
        rowCount,
        rowKeys,
        status: 'non-numeric-or-invalid-date',
        resolvedValueType: value instanceof Date ? 'date' : typeof value,
      };
    }

    return {
      dependencyId,
      path,
      rowCount,
      rowKeys,
      status: 'resolved',
      resolvedValueType: value instanceof Date ? 'date' : typeof value,
    };
  }

  private static serializeMathReferenceValue(value: unknown): string | null {
    if (value === undefined || value === null) return null;
    if (typeof value === 'number') {
      return isNaN(value) ? null : String(value);
    }

    if (typeof value === 'string') {
      const numericValue = Number(value);
      if (!isNaN(numericValue) && value.trim() !== '') {
        return String(numericValue);
      }

      const parsedTime = Date.parse(value);
      if (!isNaN(parsedTime)) {
        return JSON.stringify(new Date(parsedTime).toISOString());
      }

      return null;
    }

    if (value instanceof Date) {
      return JSON.stringify(value.toISOString());
    }

    return null;
  }

  /**
   * Detects circular dependencies in computed expressions via BFS.
   * Returns true if a cycle is found starting from startId.
   */
  private static detectCycle(startId: string, allComponents: Component[]): boolean {
    const startComp = allComponents.find(c => c.id === startId);
    const startExpr = (startComp?.properties as Record<string, unknown>)?.computedExpression;
    if (!startExpr || typeof startExpr !== 'string') return false;

    const visited = new Set<string>();
    const queue = [...this.extractComponentReferences(startExpr)];

    while (queue.length > 0) {
      const id = queue.shift()!;
      if (id === startId) return true;
      if (visited.has(id)) continue;
      visited.add(id);

      const comp = allComponents.find(c => c.id === id);
      const expr = (comp?.properties as Record<string, unknown>)?.computedExpression;
      if (expr && typeof expr === 'string') {
        queue.push(...this.extractComponentReferences(expr));
      }
    }
    return false;
  }
}
